import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import { applyPatientMutationToCache } from '@/lib/patientQueryCache';
import {
  primaryTelephoneNumber,
  readTelephoneSubscribers,
  type MedcomTelephoneSubscriber,
} from '@/lib/patientStamkortFields';
import type { Database, Json } from '@/types/database.generated';

export type PatientRow = Database['public']['Tables']['patients']['Row'];
export type PatientStatus = Database['public']['Enums']['patient_status'];

export interface PatientListItem {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  telephone_subscribers: MedcomTelephoneSubscriber[];
  status: PatientStatus;
  has_cpr: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
  clinic_id: string;
  clinic_name?: string;
}

export interface CreatePatientInput {
  clinic_id: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  cpr?: string;
  address?: {
    street?: string;
    city?: string;
    zip_code?: string;
    country?: string;
  };
  notes?: string;
  tags?: string[];
  status?: PatientStatus;
  marketing_consent_email?: boolean;
  marketing_consent_sms?: boolean;
  job_title?: string | null;
  telephone_subscribers?: { identifier: string; code: string }[] | null;
  [key: string]: unknown;
}

export interface UpdatePatientInput {
  patient_id: string;
  first_name?: string;
  last_name?: string;
  email?: string | null;
  phone?: string | null;
  cpr?: string | null;
  address?: {
    street?: string;
    city?: string;
    zip_code?: string;
    country?: string;
  };
  notes?: string | null;
  cave?: string | null;
  tags?: string[];
  status?: PatientStatus;
  marketing_consent_email?: boolean;
  marketing_consent_sms?: boolean;
  job_title?: string | null;
  telephone_subscribers?: { identifier: string; code: string }[] | null;
  sms_consent?: {
    status: 'not_set' | 'accepted' | 'declined' | 'revoked';
    source?: 'clinic_admin' | 'patient_portal' | 'consent_request_reply' | 'public_booking' | 'cutover_migration';
    updated_at?: string;
  };
  [key: string]: unknown;
}

export interface PatientMutationOutput {
  id: string;
  clinic_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  has_cpr: boolean;
  address: Record<string, unknown>;
  notes: string | null;
  cave: string | null;
  tags: string[];
  status: PatientStatus;
  marketing_consent_email: boolean;
  marketing_consent_sms: boolean;
  updated_at: string;
}

export interface PatientListFilters {
  search?: string;
  status?: PatientStatus;
  /** Only patients linked to this user in patient_staff_assignments ("Kun mine patienter"). */
  assignedToUserId?: string;
}

export const patientsListQueryKey = (
  clinicIds: string[] | string | null,
  filters: PatientListFilters,
) => ['patients', 'list', clinicIds, filters] as const;

export const patientDetailQueryKey = (patientId: string | null | undefined) =>
  ['patients', 'detail', patientId] as const;

const PATIENT_LIST_SELECT =
  'id, first_name, last_name, email, phone, telephone_subscribers, status, encrypted_cpr, tags, created_at, updated_at, clinic_id, clinics!inner(name)' as const;

export function usePatientsList(
  clinicIds: string[] | string | null | undefined,
  filters: PatientListFilters,
) {
  // Normalize to array
  const clinicIdsArray = useMemo(() => {
    if (!clinicIds) return [];
    return Array.isArray(clinicIds) ? clinicIds : [clinicIds];
  }, [clinicIds]);

  const enabled = clinicIdsArray.length > 0;

  return useQuery({
    queryKey: patientsListQueryKey(clinicIdsArray, filters),
    enabled,
    queryFn: async (): Promise<PatientListItem[]> => {
      if (clinicIdsArray.length === 0) return [];

      const assignedTo = filters.assignedToUserId;
      let query = supabase
        .from('patients')
        // "Only my patients" adds an inner join on the patient's staff links; the row
        // shape read below is the same, so the base select string types the result.
        .select(
          (assignedTo
            ? `${PATIENT_LIST_SELECT}, patient_staff_assignments!inner(user_id)`
            : PATIENT_LIST_SELECT) as typeof PATIENT_LIST_SELECT,
        )
        .in('clinic_id', clinicIdsArray)
        .eq('status', filters.status ?? 'active')
        .order('last_name', { ascending: true })
        .order('first_name', { ascending: true })
        .limit(500);

      if (assignedTo) {
        query = query.eq('patient_staff_assignments.user_id', assignedTo);
      }

      const search = filters.search?.trim();
      if (search) {
        if (search.length <= 2) {
          // ilike fallback for very short queries (1-2 chars) where FTS is noisy
          const pattern = `%${search}%`;
          query = query.or(
            `first_name.ilike.${pattern},last_name.ilike.${pattern},email.ilike.${pattern},phone.ilike.${pattern},telephone_search.ilike.${pattern}`,
          );
        } else {
          const tsQuery = search
            .split(/\s+/)
            .filter((t) => t.length > 0)
            .map((t) => `${t}:*`)
            .join(' & ');
          if (tsQuery) {
            query = query.textSearch('search_tsv', tsQuery, { config: 'simple' });
          }
        }
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data ?? []).map(
        (row): PatientListItem => ({
          id: row.id,
          first_name: row.first_name,
          last_name: row.last_name,
          email: row.email,
          phone: primaryTelephoneNumber(row.telephone_subscribers, row.phone),
          telephone_subscribers: readTelephoneSubscribers(row.telephone_subscribers),
          status: row.status,
          has_cpr: row.encrypted_cpr !== null && row.encrypted_cpr !== undefined,
          tags: row.tags ?? [],
          created_at: row.created_at,
          updated_at: row.updated_at,
          clinic_id: row.clinic_id,
          clinic_name: (row.clinics as any)?.name,
        }),
      );
    },
    staleTime: 30_000,
  });
}

export type PatientDetailRow = PatientRow & { has_cpr: boolean };

function toPatientDetail(data: PatientRow): PatientDetailRow {
  return {
    ...data,
    has_cpr: data.encrypted_cpr !== null,
  };
}

export const patientClinicIdQueryKey = (patientId: string | null | undefined) =>
  ['patients', 'clinic-id', patientId ?? null] as const;

/**
 * Patient row for the open record.
 * Pass clinicId to read only that clinic. A patient in another clinic comes back
 * as null, so the page can refuse to render their data. Omit clinicId for
 * callers that already hold a record from the clinic they are working in.
 */
export function usePatient(
  patientId: string | null | undefined,
): UseQueryResult<PatientDetailRow>;
export function usePatient(
  patientId: string | null | undefined,
  clinicId: string | null,
): UseQueryResult<PatientDetailRow | null>;
export function usePatient(patientId: string | null | undefined, clinicId?: string | null) {
  const scoped = clinicId !== undefined;
  return useQuery({
    queryKey: scoped
      ? (['patients', 'detail', patientId ?? null, clinicId] as const)
      : patientDetailQueryKey(patientId ?? null),
    enabled: Boolean(patientId) && (!scoped || Boolean(clinicId)),
    queryFn: async (): Promise<PatientDetailRow | null> => {
      if (!patientId) throw new Error('No patient id');
      if (scoped) {
        if (!clinicId) return null;
        const { data, error } = await supabase
          .from('patients')
          .select('*')
          .eq('id', patientId)
          .eq('clinic_id', clinicId)
          .maybeSingle();
        if (error) throw error;
        return data ? toPatientDetail(data as PatientRow) : null;
      }
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('id', patientId)
        .single();
      if (error) throw error;
      return toPatientDetail(data as PatientRow);
    },
    staleTime: 10_000,
  });
}

/** id and clinic only, used to offer a clinic switch without rendering the record. */
export function usePatientClinicId(patientId: string | null | undefined) {
  return useQuery({
    queryKey: patientClinicIdQueryKey(patientId),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<{ id: string; clinic_id: string } | null> => {
      if (!patientId) return null;
      const { data, error } = await supabase
        .from('patients')
        .select('id, clinic_id')
        .eq('id', patientId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 30_000,
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePatientInput): Promise<PatientMutationOutput> => {
      return await callFunction<CreatePatientInput, PatientMutationOutput>('create-patient', input);
    },
    onSuccess: (row) => applyPatientMutationToCache(qc, row),
  });
}

export function useUpdatePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdatePatientInput): Promise<PatientMutationOutput> => {
      return await callFunction<UpdatePatientInput, PatientMutationOutput>('update-patient', input);
    },
    onSuccess: (row) => applyPatientMutationToCache(qc, row, { detail: true, relations: true }),
  });
}

export function useArchivePatient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      patient_id: string;
      archive: boolean;
    }): Promise<PatientMutationOutput> => {
      return await callFunction<UpdatePatientInput, PatientMutationOutput>('update-patient', {
        patient_id: input.patient_id,
        status: input.archive ? 'archived' : 'active',
      });
    },
    onSuccess: (row) => applyPatientMutationToCache(qc, row, { detail: true }),
  });
}

export interface DecryptCprResult {
  cpr: string;
}

const PATIENT_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function usePatientAge(patientId: string | null | undefined) {
  const usableId = Boolean(patientId && PATIENT_UUID_RE.test(patientId));
  return useQuery({
    queryKey: ['patients', 'age', patientId] as const,
    enabled: usableId,
    queryFn: async (): Promise<number | null> => {
      if (!patientId || !PATIENT_UUID_RE.test(patientId)) return null;
      const result = await callFunction<{ patient_id: string }, { age: number | null }>(
        'get-patient-age',
        { patient_id: patientId },
      );
      return result.age;
    },
    staleTime: 60_000,
  });
}

export function useDecryptCpr() {
  return useMutation({
    mutationFn: async (input: {
      patient_id: string;
      source: string;
      purpose?: string;
    }): Promise<DecryptCprResult> => {
      return await callFunction<typeof input, DecryptCprResult>('decrypt-cpr', input);
    },
  });
}

// Helper to satisfy unused-export linter on Json
export type { Json };
